import { LEARNING_PHASES } from "../constants/learningPhases";
import { formatLearningDate } from "./formatLearningDate";

/**
 * Maps invite fields from `GET ld-invitation/recipient-package`.
 *
 * Response keys:
 * - locked
 * - last_activity_type — completed | fail | ...
 * - last_result_status — pass | fail (optional)
 * - attempts
 * - score.score_percentage, score.total_score, score.survey_total_score
 * - completed_at (optional)
 * - email
 * - response_id
 *
 * Load rules:
 * - locked + last_activity_type completed → SuccessStep
 * - locked + last_activity_type locked → LockedStep
 *   (backend always sends last_activity_type "locked" when locked is true for max-attempts lockout)
 * - !locked + attempts null/0 → CourseIntro (no hydrate)
 * - !locked + attempts > 0 → CourseIntro, hydrate attemptsUsed
 */

function readLocked(inviteData) {
  const raw = inviteData?.locked;

  if (raw === true || raw === 1 || raw === "1" || raw === "true") {
    return true;
  }

  if (raw === false || raw === 0 || raw === "0" || raw === "false") {
    return false;
  }

  return false;
}

function readAttempts(inviteData) {
  const raw = inviteData?.attempts;

  if (raw == null) {
    return null;
  }

  const num = Number(raw);
  return Number.isFinite(num) && num > 0 ? num : null;
}

function readLastActivityType(inviteData) {
  const raw = inviteData?.last_activity_type;
  return raw ? String(raw).toLowerCase() : "";
}

function readLastResultStatus(inviteData) {
  const raw = inviteData?.last_result_status;
  return raw ? String(raw).toLowerCase() : "";
}

function readScore(inviteData) {
  const score = inviteData?.score;
  if (!score || typeof score !== "object") {
    return {
      scorePercent: 0,
      correctCount: 0,
      totalQuestions: 0,
    };
  }

  return {
    scorePercent: Number(score.score_percentage ?? 0),
    correctCount: Number(score.total_score ?? 0),
    totalQuestions: Number(score.survey_total_score ?? 0),
  };
}

function readCompletedAt(inviteData) {
  const raw = inviteData?.last_activity_at ?? null;

  return raw
    ? formatLearningDate(new Date(raw))
    : formatLearningDate(new Date());
}

function readRecipientEmail(inviteData) {
  const email = inviteData?.email;
  return typeof email === "string" ? email.trim() : "";
}

export function mapLearningInviteRecipientEmail(inviteData) {
  if (!inviteData || typeof inviteData !== "object") {
    return "";
  }

  return readRecipientEmail(inviteData);
}

function readResponseId(inviteData) {
  const raw = inviteData?.response_id;
  return raw ? String(raw) : "";
}

function isPassedOutcome({ lastActivityType, lastResultStatus }) {
  return (
    lastActivityType === "completed" ||
    ["pass", "passed", "success"].includes(lastResultStatus)
  );
}

function buildQuizResult({
  lastResultStatus,
  scorePercent,
  correctCount,
  totalQuestions,
  attemptsUsed,
  completedAt,
  passed,
  email,
  responseId,
}) {
  return {
    scorePercent,
    correctCount,
    totalQuestions,
    resultStatus: passed ? "pass" : lastResultStatus || "fail",
    attemptNumber: attemptsUsed,
    completedAt,
    passed,
    email,
    responseId,
  };
}

function resolvePhaseOnLoad({ locked, lastActivityType, lastResultStatus }) {
  if (locked && lastActivityType === "locked") {
    return LEARNING_PHASES.LOCKED;
  }

  if (locked && isPassedOutcome({ lastActivityType, lastResultStatus })) {
    return LEARNING_PHASES.SUCCESS;
  }

  return LEARNING_PHASES.INTRO;
}

/** Returns Redux session fields to hydrate on load, or null when no invite progress exists. */
export function mapLearningInviteProgress(inviteData) {
  if (!inviteData || typeof inviteData !== "object") {
    return null;
  }

  const locked = readLocked(inviteData);
  const attempts = readAttempts(inviteData);
  const lastActivityType = readLastActivityType(inviteData);
  const lastResultStatus = readLastResultStatus(inviteData);
  const { scorePercent, correctCount, totalQuestions } = readScore(inviteData);
  const completedAt = readCompletedAt(inviteData);
  const recipientEmail = readRecipientEmail(inviteData);
  const responseId = readResponseId(inviteData);

  if (!locked && attempts == null) {
    return null;
  }

  const attemptsUsed = attempts ?? 0;
  const phase = resolvePhaseOnLoad({
    locked,
    lastActivityType,
    lastResultStatus,
  });
  const hasOutcomePhase =
    phase === LEARNING_PHASES.SUCCESS || phase === LEARNING_PHASES.LOCKED;
  const passed = phase === LEARNING_PHASES.SUCCESS;

  return {
    attemptsUsed,
    phase,
    quizResult: hasOutcomePhase
      ? buildQuizResult({
          lastResultStatus,
          scorePercent,
          correctCount,
          totalQuestions,
          attemptsUsed,
          completedAt,
          passed,
          email: recipientEmail,
          responseId,
        })
      : null,
    isLocked: phase === LEARNING_PHASES.LOCKED,
  };
}
